import type { CalendarClient } from '@wtfalch/calendar/calendar';
import { createCalendarClient } from '@wtfalch/calendar/calendar';
import type { ChatClient } from '@wtfalch/chat';
import { createChatClient } from '@wtfalch/chat';
import {
  Button,
  Empty,
  Field,
  Input,
  OrgMark,
  Pill,
  Sticker,
  ToastHost,
  useToast,
  Wordmark,
} from '@wtfalch/design';
import { identities as fetchIdentities, MailClient } from '@wtfalch/email/mailbox';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  type Agora,
  addAgora,
  agoraOrigin,
  callsDeviceId,
  callsEnabled,
  callsUrl,
  chatDeviceId,
  chatEnabled,
  chatExchangeUrl,
  encryptedChannelsEnabled,
  getLastAgoraId,
  hoursEnabled,
  hoursUrl,
  loadAgoras,
  mailSessionUrl,
  resolveAgora,
  saveAgoras,
  setLastAgoraId,
  signInChanged,
  tasksEnabled,
} from './agoras.ts';
import {
  authForAgora,
  clearPendingSignIn,
  endIssuerSession,
  hasAudience,
  isJwtShaped,
  needsRefresh,
  readPendingSignInAgoraId,
  type Session,
  startSignIn,
  startSilentSignIn,
  tokenSubject,
} from './auth.ts';
import { Calendar } from './calendar/Calendar.tsx';
import { createCallsBinding } from './calls/binding.ts';
import { GuestCall } from './calls/GuestCall.tsx';
import type { RoomCallBinding } from './calls/roomCalls.ts';
import { useIncomingCalls } from './calls/useIncomingCalls.tsx';
import { Chat } from './chat/Chat.tsx';
import { deleteCryptoStores } from './chat/encryption.ts';
import { watchIdleness } from './chat/idleness.ts';
import {
  companyTotal,
  reportBadgeCount,
  watchCalendarNeedsAction,
  watchChatUnread,
  watchMailUnread,
} from './counts.ts';
import { isDesktop } from './desktop-auth.ts';
import { desktopShellVersion, isDesktopVersionSupported } from './desktop-version.ts';
import { Files } from './files/Files.tsx';
import { fakeFiles } from './files/fake.ts';
import { realFiles } from './files/real.ts';
import type { FilesSource } from './files/source.ts';
import type { ColleagueHoursSource } from './hours/ColleagueHours.tsx';
import { createHoursClient } from './hours/client.ts';
import { Mail } from './mail/Mail.tsx';
import { watchChatNotifications } from './notify.ts';
import { newVersionReady } from './release/newVersion.ts';
import { showReminderNotification, watchReminders } from './reminders.ts';
import {
  type AgoraRoute,
  parse,
  pathForAdd,
  pathForAgora,
  pathForCalendar,
  pathForChat,
  pathForFiles,
  pathForHome,
  pathForMail,
  pathForMailCompose,
  pathForRoute,
  pathForSettings,
  pathForTasks,
} from './routes.ts';
import { ChatSearchResults } from './search/ChatSearchResults.tsx';
import type { SearchFieldClients } from './search/SearchField.tsx';
import { Settings } from './settings/Settings.tsx';
import { AgoraShell } from './shell/AgoraShell.tsx';
import { Board } from './shell/Board.tsx';
import { UpdateRequired } from './shell/UpdateRequired.tsx';
import { fakeTasks } from './tasks/fake.ts';
import { realTasks } from './tasks/real.ts';
import { TASKS_OFF_MESSAGE, type TasksSource } from './tasks/source.ts';
import { Tasks } from './tasks/Tasks.tsx';
import { Today } from './today/Today.tsx';
import { PeopleProvider, type PeopleSource } from './ui/people.tsx';

const NO_SEARCH_CLIENTS: SearchFieldClients = {
  mail: null,
  calendar: null,
  chat: null,
  files: null,
};

/** Forces the fake `FilesSource` even for an agora whose config carries a
 *  `filesProjectId` -- for tests and the preview, the same "off unless told
 *  otherwise" shape as every other unfinished integration in this app once
 *  was. Everywhere else, an agora with `filesProjectId` set now gets
 *  `realFiles()` (agora#353); one without it still gets nothing, the same
 *  as before. */
const FILES_FAKE = import.meta.env.VITE_AGORA_FILES_FAKE === '1';

/** The same switch for Tasks: the SDK's fake, for tests and the preview. */
const TASKS_FAKE = import.meta.env.VITE_AGORA_TASKS_FAKE === '1';

/** Whether Files is both configured for this agora and actually usable by
 *  this session's own token. The browser's own `authForAgora` asks for the
 *  Files audience whenever `filesProjectId` is set, but a desktop deploy
 *  requests it independently, from its own `FILES_PROJECT_ID` env var
 *  (`apps/desktop-auth`) -- which can lag the browser config during a Files
 *  rollout (agora#418). Showing the tile, or building a `realFiles()`
 *  client, off config alone would then read as "Files is on" while the
 *  token behind it carries no audience for it and every request fails.
 *  There is no "no session" case to weigh: both call sites below already
 *  have one, the rail's own tile only ever drawn once there is a session
 *  (`AgoraShell`'s `current` is null otherwise) and `AgoraView` not
 *  rendered without one either.
 *
 *  This agora's own ZITADEL app mints an opaque access token on purpose
 *  (`authForAgora`'s own doc), which carries no `aud` this function can
 *  read at all -- decoding it always answered false, so the Files tile
 *  never showed (agora#505, auth#321). Only a JWT-shaped token (a desktop
 *  deploy's lagging config, still the scenario agora#418 guards) can
 *  actually prove a missing audience; an opaque one proves nothing either
 *  way, so this trusts the agora's own config for it instead, the same way
 *  `realFiles()`'s every request is still checked server-side regardless of
 *  what this function answers. */
function filesReady(agora: Pick<Agora, 'filesProjectId'>, session: Session): boolean {
  if (!agora.filesProjectId) return false;
  if (!isJwtShaped(session.accessToken)) return true;
  return hasAudience(session.accessToken, agora.filesProjectId);
}

/**
 * The shell: `shell/AgoraShell.tsx` around one company's four sections, and a
 * board (`shell/Board.tsx`) for every state before a company is open:
 * signing in, signed out, adding a company, a link to a company this browser
 * has not added, offline.
 */
export function App() {
  const [path, setPath] = useState(() => window.location.pathname);
  const [agoras, setAgoras] = useState<Agora[]>(() => loadAgoras());
  const [online, setOnline] = useState(() => navigator.onLine);
  const [agoraAccount, setAgoraAccount] = useState<{ name: string | null; address: string } | null>(
    null,
  );
  const [agoraChatAvailable, setAgoraChatAvailable] = useState<boolean | null>(null);
  const [agoraActions, setAgoraActions] = useState<React.ReactNode>(null);
  const [agoraTitle, setAgoraTitle] = useState<string | null>(null);
  const [agoraMailUnread, setAgoraMailUnread] = useState<number | null>(null);
  const [agoraChatUnread, setAgoraChatUnread] = useState<number | null>(null);
  const [agoraCalendarUnread, setAgoraCalendarUnread] = useState<number | null>(null);
  const [agoraSearchClients, setAgoraSearchClients] =
    useState<SearchFieldClients>(NO_SEARCH_CLIENTS);

  // The incompatible-version screen (agora#37): checked once, against
  // whatever the desktop shell's own bridge reports, and never in a browser
  // tab -- `desktopShellVersion()` only reads `window.wtfalchDesktop` at
  // all when `isDesktop()` says there is one. Starts optimistic (true) so a
  // desktop window never flashes this screen while the one-time IPC round
  // trip to ask the shell its own version is still in flight.
  const [desktopSupported, setDesktopSupported] = useState(true);
  useEffect(() => {
    if (!isDesktop()) return;
    let live = true;
    void desktopShellVersion().then((version) => {
      if (live) setDesktopSupported(isDesktopVersionSupported(version));
    });
    return () => {
      live = false;
    };
  }, []);

  // The desktop dock's own badge (agora#219): every section's count summed,
  // reported whenever any of them changes. `reportBadgeCount` itself is a
  // no-op in a browser tab -- there is no `window.wtfalchDesktop` there.
  useEffect(() => {
    reportBadgeCount(
      companyTotal({ mail: agoraMailUnread, chat: agoraChatUnread, calendar: agoraCalendarUnread }),
    );
  }, [agoraMailUnread, agoraChatUnread, agoraCalendarUnread]);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const go = useCallback((next: string) => {
    if (newVersionReady()) {
      window.location.assign(next);
      return;
    }
    window.history.pushState(null, '', next);
    setPath(next);
  }, []);

  const replace = useCallback((next: string) => {
    window.history.replaceState(null, '', next);
    setPath(next);
  }, []);

  const route = useMemo(() => parse(path), [path]);
  const current =
    route.kind === 'agora' || route.kind === 'settings'
      ? (agoras.find((a) => a.id === route.agoraId) ?? null)
      : null;

  const rememberAgora = useCallback((agora: Agora) => {
    setAgoras((prev) => {
      const next = addAgora(prev, agora);
      saveAgoras(next);
      return next;
    });
    setLastAgoraId(agora.id);
  }, []);

  // A saved agora is a copy of its `.well-known` config from the day it was
  // added. Re-read it on every open, so a project the agora has since moved
  // to reaches this browser: until then every token asks for the old
  // audience and that service refuses it (chat's `wrong_audience`).
  useEffect(() => {
    if (!current) return;
    let live = true;
    void resolveAgora(current.address).then(
      (fresh) => {
        if (!live || JSON.stringify(fresh) === JSON.stringify(current)) return;
        if (signInChanged(current, fresh)) {
          sessionGeneration.current += 1;
          void authForAgora(current)
            .forget()
            .catch(() => {});
          setSession(null);
        }
        setAgoras((prev) => {
          const next = prev.map((agora) => (agora.id === fresh.id ? fresh : agora));
          saveAgoras(next);
          return next;
        });
      },
      () => {},
    );
    return () => {
      live = false;
    };
  }, [current]);

  // The session is App's, not the open section's.
  //
  // `AgoraShell` below decides from `current` whether to draw the place rail,
  // and `current` is only ever the route's agora -- so while the session lived
  // inside `AgoraView`, the rail, the company mark and the section title were
  // drawn around a signed-out person with the sign-in card sitting in the
  // card's body. The answer has to live where the shell can see it.
  //
  // Seeded synchronously from the agora's own store rather than in an effect,
  // and re-seeded during render when the open agora changes, so somebody who
  // is already signed in never gets a frame of railless app first.
  // Memoised on the sign-in fields alone, not on `current`'s identity: the
  // config-refresh effect above replaces `current` with a new object on any
  // field change, including cosmetic ones (name, theme). Rebuilding `auth`
  // then would reseed `session`/`signingIn`/`silentTried` below on every such
  // rewrite, not only the ones `signInChanged` already treats as a new
  // sign-in.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  const auth = useMemo(
    () => (current ? authForAgora(current) : null),
    [
      current?.id ?? null,
      current?.issuer ?? null,
      current?.clientId ?? null,
      current?.organisationId ?? null,
      current?.mailProjectId ?? null,
      current?.messagingProjectId ?? null,
      current?.filesProjectId ?? null,
    ],
  );
  const [sessionAuth, setSessionAuth] = useState(auth);
  const [session, setSession] = useState<Session | null>(() => auth?.currentSession() ?? null);
  // Settings sits outside `AgoraView`, so it builds its own working-hours
  // client; the ref keeps the bearer current across refreshes without
  // making a new client (and a refetch) each time (agora#258).
  const settingsTokenRef = useRef('');
  settingsTokenRef.current = session?.accessToken ?? '';
  const hoursAddress = current && hoursEnabled(current) ? current.address : null;
  const settingsHours = useMemo(
    () =>
      hoursAddress
        ? createHoursClient({
            url: hoursUrl({ address: hoursAddress }),
            getAccessToken: () => settingsTokenRef.current,
          })
        : undefined,
    [hoursAddress],
  );
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [silentTried, setSilentTried] = useState(false);
  if (auth !== sessionAuth) {
    setSessionAuth(auth);
    setSession(auth?.currentSession() ?? null);
    setSigningIn(false);
    setSignInError(null);
    setSilentTried(false);
  }

  // A committed auth client owns this generation of session state. Layout
  // cleanup invalidates it before another company's effects start, including
  // A -> B -> A and unmounts. Sign-out and a new sign-in invalidate it too.
  const sessionOwner = useRef(auth);
  const sessionGeneration = useRef(0);
  useLayoutEffect(() => {
    sessionOwner.current = auth;
    sessionGeneration.current += 1;
    return () => {
      sessionOwner.current = null;
      sessionGeneration.current += 1;
    };
  }, [auth]);
  const captureSession = useCallback(() => {
    const generation = sessionGeneration.current;
    return () =>
      !!auth && sessionOwner.current === auth && sessionGeneration.current === generation;
  }, [auth]);

  /** Where `signIn` comes back to: the section that asked for it. */
  const returnPath =
    route.kind === 'settings'
      ? pathForSettings(route.agoraId)
      : route.kind !== 'agora'
        ? pathForHome()
        : route.section === 'mail'
          ? pathForMail(route.agoraId, route.location)
          : route.section === 'chat'
            ? pathForChat(route.agoraId, route.location)
            : route.section === 'calendar'
              ? pathForCalendar(route.agoraId, route.location)
              : route.section === 'files'
                ? pathForFiles(route.agoraId, route.location)
                : route.section === 'tasks'
                  ? pathForTasks(route.agoraId, route.location)
                  : pathForAgora(route.agoraId);

  const signIn = useCallback(
    (loginHint?: string) => {
      if (!auth || !current || !captureSession()()) return;
      sessionGeneration.current += 1;
      const ownsSession = captureSession();
      setSigningIn(true);
      setSignInError(null);
      // A press ends the silent attempt for this company. The press clears the
      // mark `silentSignInAvailable` reads, and it resolves before the browser
      // has left; a silent request started then would leave last, and the
      // issuer would answer `login_required` instead of showing its page.
      setSilentTried(true);
      void startSignIn(auth, current.id, returnPath, loginHint)
        .then(() => {
          if (ownsSession() && isDesktop()) setSession(auth.currentSession());
        })
        .catch(() => {
          if (ownsSession()) setSignInError('Sign-in did not complete. Try again.');
        })
        .finally(() => {
          if (ownsSession()) setSigningIn(false);
        });
    },
    [auth, current, returnPath, captureSession],
  );

  // Before drawing a sign-in screen, ask the issuer once whether this person
  // is already signed in there. Until that answer is known the screen says
  // so, rather than flashing a sign-in card at somebody about to be let in.
  const silentPending = !session && !silentTried && !!auth?.silentSignInAvailable();
  useEffect(() => {
    if (!silentPending || !auth || !current) return;
    const ownsSession = captureSession();
    let live = true;
    void startSilentSignIn(auth, current.id, returnPath)
      .then((left) => {
        if (live && ownsSession() && !left) setSilentTried(true);
      })
      .catch(() => {
        if (live && ownsSession()) setSilentTried(true);
      });
    return () => {
      live = false;
    };
  }, [silentPending, auth, current, returnPath, captureSession]);

  const signOut = useCallback(() => {
    if (!auth || !captureSession()()) return;
    sessionGeneration.current += 1;
    const ownsSession = captureSession();
    // The crypto keys stay on this browser otherwise (agora#135).
    const keysDeleted = current ? deleteCryptoStores(current.id) : Promise.resolve();
    void auth
      .forget()
      // Only after the token and the keys are gone: the browser leaves here.
      // The wait is bounded because a store still open holds its delete back.
      // The desktop bridge ends its own session in `forget`.
      .then(async () => {
        await Promise.race([keysDeleted, new Promise((done) => setTimeout(done, 1000))]);
        if (ownsSession() && current && !isDesktop()) endIssuerSession(current);
      })
      .catch(() => {
        // forget() itself is what rejected here -- the local drop (or, on
        // desktop, the bridge's unified local-and-issuer sign-out) failed,
        // so the issuer session was never reached either (agora#450).
        if (ownsSession()) setSignInError('Sign-out could not be completed. Try again.');
      });
    setSession(null);
    setSigningIn(false);
  }, [auth, current, captureSession]);

  // null means the caller's session lifecycle ended, so it must not retry sign-in.
  const refresh = useCallback(async (): Promise<boolean | null> => {
    const ownsSession = captureSession();
    if (!auth || !ownsSession()) return null;
    // A sign-out (or new sign-in) that invalidated this call can still lose
    // the race to a same-instance refresh already past the auth client's own
    // guard, landing a rotated token in storage after `forget()` already
    // ran. Only ever drop it on the same instance this call started with --
    // a company switch leaves `sessionOwner` pointing at a different `auth`,
    // and forgetting that one would erase a session this call has no
    // business touching.
    const dropStaleRotation = async () => {
      if (sessionOwner.current === auth) await auth.forget().catch(() => {});
    };
    try {
      const result = await auth.refresh();
      if (!ownsSession()) {
        if (result.kind === 'refreshed') await dropStaleRotation();
        return null;
      }
      if (result.kind === 'refreshed') {
        setSession(result.session);
        return true;
      }
      if (!isDesktop()) await auth.forget();
      if (!ownsSession()) return null;
      setSession(null);
      return false;
    } catch (error) {
      if (!ownsSession()) return null;
      throw error;
    }
  }, [auth, captureSession]);

  // The desktop app restores its session from the host rather than from a
  // redirect, so this runs whether or not there is a session yet -- it is how
  // a desktop window gets one. It lives here, with the rest of acquiring a
  // session; `AgoraView` is not even mounted while signed out any more.
  useEffect(() => {
    if (!auth || !isDesktop()) return;
    const ownsSession = captureSession();
    let live = true;
    void auth
      .refresh()
      .then((result) => {
        if (live && ownsSession() && result.kind === 'refreshed') setSession(result.session);
      })
      .catch(() => {
        if (live && ownsSession())
          setSignInError('Could not restore your desktop session. Try again.');
      });
    return () => {
      live = false;
    };
  }, [auth, captureSession]);

  // A session close to expiry is refreshed before it is used, not after it
  // fails -- a token that dies mid-request is worse than one renewed early.
  useEffect(() => {
    if (!session) return;
    const ownsSession = captureSession();
    const refreshSession = () => {
      if (!ownsSession()) return;
      void refresh().catch(() => {
        if (ownsSession()) setSignInError('Could not refresh your session. Try again.');
      });
    };
    if (!isDesktop()) {
      if (needsRefresh(session)) refreshSession();
      return;
    }
    const timer = window.setTimeout(
      refreshSession,
      needsRefresh(session) ? 10_000 : Math.max(0, session.expiresAt - Date.now() - 60_000),
    );
    return () => window.clearTimeout(timer);
  }, [session, refresh, captureSession]);

  let body: React.ReactNode;
  if (!desktopSupported) {
    body = <UpdateRequired />;
  } else if (!online) {
    body = (
      <Board title="Offline" centered>
        <p>Reconnect to pick up where you left off.</p>
        <Button onPress={() => window.location.reload()}>Try again</Button>
      </Board>
    );
  } else if (route.kind === 'guest-call') {
    body = <GuestCall />;
  } else if (route.kind === 'callback') {
    body = <Callback agoras={agoras} replace={replace} />;
  } else if (route.kind === 'add') {
    body = (
      <AddAgora
        title="Add a company"
        onAdded={(agora) => {
          rememberAgora(agora);
          go(pathForAgora(agora.id));
        }}
      />
    );
  } else if (route.kind === 'home') {
    body = (
      <Home
        agoras={agoras}
        replace={replace}
        go={go}
        onAdded={(agora) => {
          rememberAgora(agora);
          go(pathForAgora(agora.id));
        }}
      />
    );
  } else if (!current) {
    body = (
      <Board title="This company is not added here">
        <p>
          The link names a company this browser has not added. Add it by its address to open it.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button kind="primary" onPress={() => go(pathForAdd())}>
            Add a company
          </Button>
        </div>
      </Board>
    );
  } else if (!session && silentPending) {
    body = (
      <Board title={`Opening ${current.name}`} busy>
        <p>Checking whether you are signed in.</p>
      </Board>
    );
  } else if (!session) {
    body = <SignIn agora={current} busy={signingIn} error={signInError} onSignIn={signIn} />;
  } else if (route.kind === 'settings') {
    body = (
      <Settings agora={current} go={go} {...(settingsHours ? { hours: settingsHours } : {})} />
    );
  } else {
    body = (
      <AgoraView
        key={current.id}
        agora={current}
        session={session}
        route={route}
        go={go}
        onRefresh={refresh}
        onSignIn={signIn}
        onChatAvailable={setAgoraChatAvailable}
        onAccount={setAgoraAccount}
        onActions={setAgoraActions}
        onTitle={setAgoraTitle}
        onMailUnread={setAgoraMailUnread}
        onChatUnread={setAgoraChatUnread}
        onCalendarUnread={setAgoraCalendarUnread}
        onSearchClients={setAgoraSearchClients}
      />
    );
  }

  return (
    <ToastHost>
      <AgoraShell
        agoras={agoras}
        current={online && session ? current : null}
        section={route.kind === 'agora' ? route.section : null}
        title={online ? (agoraTitle ?? undefined) : undefined}
        chatAvailable={agoraChatAvailable}
        filesAvailable={FILES_FAKE || (current && session ? filesReady(current, session) : false)}
        mailUnread={online ? agoraMailUnread : null}
        chatUnread={online ? agoraChatUnread : null}
        calendarUnread={online ? agoraCalendarUnread : null}
        account={agoraAccount && current ? agoraAccount : null}
        onSignOut={online && session ? signOut : null}
        actions={online ? agoraActions : null}
        searchClients={online ? agoraSearchClients : NO_SEARCH_CLIENTS}
        go={go}
      >
        {body}
      </AgoraShell>
    </ToastHost>
  );
}

/** `/`: the only agora if there is exactly one, else the last one visited, else
 *  a plain choice. The redirect happens in an effect, not during render --
 *  `replace` touches history, which is a side effect a render must not
 *  perform itself. */
function Home({
  agoras,
  replace,
  go,
  onAdded,
}: {
  agoras: Agora[];
  replace: (path: string) => void;
  go: (path: string) => void;
  onAdded: (agora: Agora) => void;
}) {
  const target =
    agoras.length === 1 ? agoras[0] : agoras.find((agora) => agora.id === getLastAgoraId());
  // The address this page was served from is already the company, in a browser
  // tab as much as in a desktop window, so the first agora comes free from it.
  // Asking for an address the URL bar is showing asks the same question twice.
  // A build nobody's agora serves -- a generic installer -- gets no config
  // here, and that is the case the add form is for.
  const [selfResolve, setSelfResolve] = useState<'trying' | 'failed'>('trying');
  useEffect(() => {
    if (agoras.length) return;
    let live = true;
    void resolveAgora(window.location.host)
      .then((agora) => {
        if (live) onAdded(agora);
      })
      .catch(() => {
        if (live) setSelfResolve('failed');
      });
    return () => {
      live = false;
    };
  }, [agoras.length, onAdded]);

  useEffect(() => {
    if (target) replace(pathForAgora(target.id));
  }, [target, replace]);

  if (target) {
    return (
      <Board title={`Opening ${target.name}`} busy>
        <p>Loading it.</p>
      </Board>
    );
  }
  if (agoras.length === 0 && selfResolve === 'trying')
    return (
      <Board title="Opening your company" busy>
        <p>Loading it from this window’s address.</p>
      </Board>
    );
  if (agoras.length === 0) return <AddAgora title="Add your company" onAdded={onAdded} />;
  return (
    <Board title="Choose a company">
      <p>Each company keeps its own mail, chat, calendar and sign-in.</p>
      <ul className="grid gap-1 m-0 mb-5 p-0 list-none">
        {agoras.map((agora) => (
          <li key={agora.id}>
            <a
              className="board-company"
              href={pathForAgora(agora.id)}
              onClick={(event) => {
                event.preventDefault();
                go(pathForAgora(agora.id));
              }}
            >
              <OrgMark
                initial={agora.name.slice(0, 1).toUpperCase()}
                color={agora.theme.accent}
                size="md"
              />
              {agora.name}
            </a>
          </li>
        ))}
      </ul>
    </Board>
  );
}

function AddAgora({ title, onAdded }: { title: string; onAdded: (agora: Agora) => void }) {
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Board title={title}>
      <p>
        Type the address your company gave you. The agora reads that company's sign-in and mail
        settings from it.
      </p>
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = address.trim();
          if (!trimmed || busy) return;
          setBusy(true);
          setError(null);
          resolveAgora(trimmed)
            .then(onAdded)
            .catch((err: unknown) => {
              setError(err instanceof Error ? err.message : 'Agora config failed to load.');
            })
            .finally(() => setBusy(false));
        }}
      >
        <Field label="Company address">
          {(field) => (
            <Input
              {...field}
              name="address"
              type="text"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              placeholder="mail.example.com"
              block
            />
          )}
        </Field>
        {error && (
          <p className="board-error" role="alert">
            {error}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" kind="primary" busy={busy}>
            Add company
          </Button>
        </div>
      </form>
    </Board>
  );
}

/** What every account in the estate is true of, whichever company it belongs
 *  to. Claims about the issuer's own setup, not about this app. */
const FACTS = ['Passkeys', 'Two-factor', 'One account per person', 'No passwords stored here'];

/** The signed-out screen, and `App`'s rather than `AgoraView`'s because it has
 *  to render *outside* `AgoraShell`: somebody who is not signed in gets this
 *  and nothing else, never the place rail with a card sitting in it.
 *
 *  `Sign-in.dc.html` on the design canvas. What the canvas draws and this does
 *  not: an email field, a passkey button and a "get a magic link" line. Signing
 *  in here is one redirect to the company's own issuer, which owns every one of
 *  those choices -- an email box on this page would collect a value nothing
 *  reads. The panel carries the canvas; the card carries the one real action. */
function SignInCard({
  agora,
  busy,
  error,
  onSignIn,
}: {
  agora: Agora;
  busy: boolean;
  error: string | null;
  onSignIn: () => void;
}) {
  return (
    <div className="signin">
      <section className="signin-tell">
        <Wordmark name="Agora" size="lg" />
        <div className="signin-say grid gap-5 content-center justify-items-start">
          <Sticker ground="peach" size="lg">
            <img
              src="/sign-in.svg"
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                padding: 'var(--space-2)',
              }}
            />
          </Sticker>
          <h1 className="signin-head">Everything you do, and everyone you do it with.</h1>
          <p className="signin-lead">Your whole day, already here and waiting.</p>
        </div>
        <ul className="flex flex-wrap gap-2 m-0 p-0 list-none">
          {FACTS.map((fact) => (
            <li key={fact}>
              <Pill outline>{fact}</Pill>
            </li>
          ))}
        </ul>
      </section>

      <section className="grid place-items-center py-8 px-4">
        <div className="signin-card">
          <h2 className="signin-card-title">Sign in</h2>
          <p className="m-0 text-muted text-pretty">
            {agora.name} keeps its own accounts.{' '}
            {isDesktop() ? 'Sign-in opens in your browser.' : 'The session stays in this tab.'}
          </p>
          {error && (
            <p className="board-error" role="alert">
              {error}
            </p>
          )}
          <Button
            kind="primary"
            size="lg"
            block
            busy={busy}
            onPress={() => {
              onSignIn();
            }}
          >
            {busy ? 'Signing in…' : `Sign in to ${agora.name}`}
          </Button>
        </div>
      </section>
    </div>
  );
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The signed-out screen, and `App`'s rather than `AgoraView`'s because it has
 *  to render *outside* `AgoraShell`: somebody who is not signed in gets this
 *  and nothing else, never the place rail with a card sitting in it.
 *
 *  `Signin-Agora-email.dc.html` (and `-phone`) on the design canvas: the room
 *  on the left, the company's own form on the right. Continue leaves for the
 *  company's sign-in with the email as a hint, so its first step is skipped;
 *  the passkey link leaves without one. The line under the field is always
 *  there, so a message never moves the layout.
 *
 *  One markup for every width. On a phone (`app.css`, below 48rem) the same
 *  elements stack centred, as `Signin-Agora-phone.dc.html`: the mark, the
 *  picture, the headline, the card, then the new-here line. CSS only. */
export function SignInEmail({
  agora,
  busy,
  error,
  onSignIn,
}: {
  agora: Agora;
  busy: boolean;
  error: string | null;
  onSignIn: (loginHint?: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [via, setVia] = useState<'email' | 'passkey'>('email');
  const message = invalid ? 'Enter your email address, like name@company.com.' : (error ?? '');
  return (
    <div className="signin signin-email">
      <section className="signin-tell">
        <Wordmark name="Agora" size="lg" />
        <div className="signin-say grid gap-5 content-center justify-items-start">
          <Sticker ground="peach" size="lg">
            <img
              src="/sign-in.svg"
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                padding: 'var(--space-2)',
              }}
            />
          </Sticker>
          <h1 className="signin-head">Everything you do, and everyone you do it with.</h1>
          <p className="signin-lead">Your whole day, already here and waiting.</p>
        </div>
        <span aria-hidden="true" />
      </section>

      <section className="signin-side">
        <form
          className="signin-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (busy) return;
            const trimmed = email.trim();
            if (!EMAIL_SHAPE.test(trimmed)) {
              setInvalid(true);
              return;
            }
            setInvalid(false);
            setVia('email');
            onSignIn(trimmed);
          }}
        >
          <p className="signin-company">{agora.name}</p>
          <div className="signin-intro grid gap-2">
            <h2 className="signin-card-title">Sign in</h2>
            <p className="m-0 text-muted text-pretty">Use your {agora.name} account.</p>
          </div>
          <div className="grid gap-1">
            <Field label="Email">
              {(field) => (
                <Input
                  {...field}
                  name="email"
                  type="email"
                  autoComplete="username webauthn"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  value={email}
                  aria-invalid={invalid || undefined}
                  aria-describedby="signin-message"
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setInvalid(false);
                  }}
                  block
                />
              )}
            </Field>
            <p id="signin-message" className="signin-message" role="alert">
              {message}
            </p>
          </div>
          <Button type="submit" kind="primary" size="lg" block busy={busy && via === 'email'}>
            {busy && via === 'email' ? 'Signing in…' : 'Continue'}
          </Button>
          <Button
            kind="ghost"
            size="sm"
            className="justify-self-center"
            busy={busy && via === 'passkey'}
            onPress={() => {
              if (busy) return;
              setVia('passkey');
              onSignIn();
            }}
          >
            {busy && via === 'passkey' ? 'Signing in…' : 'Sign in with a passkey'}
          </Button>
        </form>
        <p className="m-0 text-muted signin-new">New here? Reach out to your contact person.</p>
      </section>
    </div>
  );
}

/** The signed-out screen. The email-first screen shows only for an agora whose
 *  config says sign-in is in this app (`signInInApp: true`) and only in a
 *  browser: the desktop shell asks for the email in its own welcome window
 *  and has no way to carry a second one into the sign-in. Every other case
 *  is the card as it was. */
function SignIn(props: {
  agora: Agora;
  busy: boolean;
  error: string | null;
  onSignIn: (loginHint?: string) => void;
}) {
  return props.agora.signInInApp === true && !isDesktop() ? (
    <SignInEmail {...props} />
  ) : (
    <SignInCard {...props} />
  );
}

/** `/callback`: find the agora whose sign-in is pending, finish the flow with
 *  that agora's own `BrowserAuth`, then return to wherever `signIn` was asked
 *  to come back to. */
function Callback({ agoras, replace }: { agoras: Agora[]; replace: (path: string) => void }) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const agoraId = readPendingSignInAgoraId();
    const agora = agoraId ? agoras.find((h) => h.id === agoraId) : undefined;
    if (!agora) {
      clearPendingSignIn();
      replace(pathForAdd());
      return;
    }
    const auth = authForAgora(agora);
    auth
      .completeSignIn(window.location.search)
      .then((result) => {
        clearPendingSignIn();
        if (!live) return;
        if (result.kind === 'signed-in') {
          replace(result.returnTo || pathForAgora(agora.id));
          return;
        }
        if (result.kind === 'silent-refused') {
          replace(pathForAgora(agora.id));
          return;
        }
        setError(result.error);
      })
      .catch(() => {
        clearPendingSignIn();
        if (live) setError('The sign-in could not be completed.');
      });
    return () => {
      live = false;
    };
  }, [agoras, replace]);

  if (error) {
    return (
      <Board title="Sign-in did not finish">
        <p>{error}</p>
        <div className="flex flex-wrap items-center gap-3">
          <Button kind="primary" onPress={() => replace(pathForHome())}>
            Back to your companies
          </Button>
        </div>
      </Board>
    );
  }
  return (
    <Board title="Signing in" busy>
      <p>Finishing sign-in with your company.</p>
    </Board>
  );
}

/** One agora's mail and chat: its own `BrowserAuth`, its own `MailClient` and
 *  (when the token carries the messaging audience) its own `ChatClient`,
 *  created only once that agora has a session and never shared with any other
 *  agora. The parent keys this component by `agora.id`, so switching agoras
 *  unmounts it entirely rather than reusing state across agoras. */
function AgoraView({
  agora,
  session,
  route,
  go,
  onRefresh,
  onSignIn,
  onChatAvailable,
  onAccount,
  onActions,
  onTitle,
  onMailUnread,
  onChatUnread,
  onCalendarUnread,
  onSearchClients,
}: {
  agora: Agora;
  /** Owned by `App`, which does not render this component without one. */
  session: Session;
  route: AgoraRoute;
  go: (path: string) => void;
  /** Rotate the token: false requires sign-in; null ends a stale caller's work. */
  onRefresh: () => Promise<boolean | null>;
  onSignIn: () => void;
  onChatAvailable: (available: boolean | null) => void;
  /** Who is signed in, for the header -- the mail SDK's own identities, the
   *  same field the composer's signature line reads. Null until they load. */
  onAccount: (account: { name: string | null; address: string } | null) => void;
  /** The open section's own header controls -- Mail's "Write", say --
   *  reported up into `AgoraShell`'s `actions` slot. */
  onActions: (actions: React.ReactNode) => void;
  /** The open section's own override of the header's title -- Mail's
   *  "Write" while its composer is open -- reported up into `AgoraShell`'s
   *  `title` prop the same way `onActions` reports its own slot. */
  onTitle: (title: string | null) => void;
  /** The Inbox's own unread-thread count, for the rail's Mail tile -- kept
   *  live by `counts.ts`'s `watchMailUnread` once a mail client exists,
   *  independent of whether Mail is the open section, since the rail is
   *  always on screen (agora#219). */
  onMailUnread: (unread: number | null) => void;
  /** Every unmuted chat room's unread count, summed
   *  (`counts.ts`'s `watchChatUnread`), for the rail's Chat tile. */
  onChatUnread: (unread: number | null) => void;
  /** Upcoming invitations not yet answered (`counts.ts`'s
   *  `watchCalendarNeedsAction`), for the rail's Calendar tile. */
  onCalendarUnread: (unread: number | null) => void;
  /** The mail and calendar clients, for the header's combined search
   *  (agora#216) -- reported up the same way as the rest of this list,
   *  since the field lives in `AgoraShell` rather than in any one section. */
  onSearchClients: (clients: SearchFieldClients) => void;
}) {
  const [refusedOnce, setRefusedOnce] = useState(false);
  const toast = useToast();

  // Created once, lazily, the first time this agora has a session -- not a
  // `useMemo` keyed on the token, because a refresh calls `setToken` on this
  // same instance below rather than replacing it. The parent remounts this
  // whole component (keyed by `agora.id`) when the agora changes, which is what
  // resets this ref for the next agora.
  const clientRef = useRef<MailClient | null>(null);
  if (!clientRef.current) {
    clientRef.current = new MailClient({
      sessionUrl: mailSessionUrl(agora),
      bearerToken: session.accessToken,
    });
  }
  const client = clientRef.current;

  // One client per account whose folders a colleague has shared, made on
  // first use and kept so `Mail` gets the same one back (and so a token
  // rotation below can reach it).
  const sharedClientsRef = useRef(new Map<string, MailClient>());
  const sessionUrl = mailSessionUrl(agora);
  const tokenNow = useRef(session.accessToken);
  tokenNow.current = session.accessToken;
  const clientFor = useCallback(
    (accountId: string) => {
      let other = sharedClientsRef.current.get(accountId);
      if (!other) {
        other = new MailClient({ sessionUrl, bearerToken: tokenNow.current, accountId });
        sharedClientsRef.current.set(accountId, other);
      }
      return other;
    },
    [sessionUrl],
  );

  // `App` rotates the token; the mail client is the one holder that caches it,
  // so it is synced here rather than from up there. The chat and calendar
  // clients each read `accessTokenRef` per request and need no telling.
  //
  // `setToken` means "this rotated", not "here is your token": the client was
  // constructed with the session it was created under, so the ref starts at
  // that value and the effect no-ops on mount. A client is only ever told
  // about a token it does not already hold -- which is what keeps one agora's
  // token from ever being announced to another agora's client.
  const syncedTokenRef = useRef(session.accessToken);
  useEffect(() => {
    if (syncedTokenRef.current === session.accessToken) return;
    syncedTokenRef.current = session.accessToken;
    client?.setToken(session.accessToken);
    for (const other of sharedClientsRef.current.values()) other.setToken(session.accessToken);
  }, [client, session.accessToken]);

  // `createChatClient` fetches a fresh token through this on every exchange
  // and every refresh; a ref (rather than a closure over `session`) is what
  // keeps that answer current across `ChatClient`'s own lifetime, since the
  // client below is created once and its `getAccessToken` is never replaced.
  const accessTokenRef = useRef(session.accessToken);
  accessTokenRef.current = session.accessToken;

  // Chat is on when the exchange says it is, not when the token looks right.
  //
  // This used to decode the access token as a JWT and look for the messaging
  // project in its `aud`. The agora's ZITADEL app mints an opaque access
  // token, on purpose, so Stalwart's userinfo path can resolve it
  // (.plans/2026-09-14-agora.md) -- and an opaque token has no payload to
  // read, so the check said no for everyone and Chat never appeared, with the
  // exchange live and willing behind it. The exchange is the authority on who
  // may chat: ask it. `start()` performs the exchange and rejects when it is
  // refused; until it answers, the rail simply has no Chat tile yet.
  const chatClientRef = useRef<ChatClient | null>(null);
  if (!chatClientRef.current && chatEnabled(agora)) {
    chatClientRef.current = createChatClient({
      exchangeUrl: chatExchangeUrl(agora),
      getAccessToken: () => accessTokenRef.current,
      deviceId: chatDeviceId(agora.id),
      storeName: agora.id,
      // Key backup, recovery key and device verification (agora#135); off
      // unless this company's config allows encrypted channels.
      encryptionEnabled: encryptedChannelsEnabled(agora),
    });
  }
  const chatClient = chatClientRef.current;

  // Calls ride on Chat rooms, so they are bound only when both flags are on.
  // The bearer is read per request through `accessTokenRef`, like Chat's.
  const callsRef = useRef<RoomCallBinding | null>(null);
  if (!callsRef.current && chatEnabled(agora) && callsEnabled(agora)) {
    callsRef.current = createCallsBinding({
      baseUrl: callsUrl(agora),
      getAccessToken: () => accessTokenRef.current,
      deviceId: callsDeviceId(agora.id),
    });
  }
  const calls = callsRef.current ?? undefined;

  const [chatState, setChatState] = useState<'asking' | 'on' | 'off'>(
    chatEnabled(agora) ? 'asking' : 'off',
  );
  useEffect(() => {
    if (!chatClient) return;
    let live = true;
    chatClient.start().then(
      () => live && setChatState('on'),
      () => live && setChatState('off'),
    );
    return () => {
      live = false;
    };
  }, [chatClient]);

  // This is the client's only `stop()`: the Chat section, the header
  // search, the idleness watcher and Today all share it but only visit it,
  // and this component -- keyed by `agora.id` -- is what actually owns its
  // whole lifetime, ending only on an agora switch or a sign-out. Holds a
  // stop scheduled by this effect's own cleanup, for the same reason
  // `chat/Chat.tsx` used to defer its own (now-removed) stop: React 19's
  // Strict Mode mounts, cleans up and mounts this effect again, all
  // synchronously, to prove an effect tolerates it -- and `ChatClient`'s own
  // `stop()` does not (a `start()` already in flight keeps running and
  // reassigns a live matrix client after `stop()` cleared it, so stopping
  // between the two mounts leaves a second, orphaned sync loop running
  // underneath the one the remount starts). A timer fires only once the
  // current synchronous work is done; the *next* mount's own effect,
  // arriving before any timer can, cancels it below -- so a genuine unmount
  // still stops promptly, and a Strict Mode double invoke never stops what
  // the very next line restarts.
  const stopChatTimer = useRef<number | null>(null);
  useEffect(() => {
    if (!chatClient) return;
    if (stopChatTimer.current !== null) {
      window.clearTimeout(stopChatTimer.current);
      stopChatTimer.current = null;
    }
    return () => {
      stopChatTimer.current = window.setTimeout(() => {
        stopChatTimer.current = null;
        chatClient.stop();
      }, 0);
    };
  }, [chatClient]);

  const chatAvailable = chatState === 'on';
  useEffect(() => {
    onChatAvailable(chatState === 'asking' ? null : chatAvailable);
    return () => onChatAvailable(false);
  }, [chatState, chatAvailable, onChatAvailable]);

  // Without this, a tab left open all night keeps syncing and Matrix keeps
  // calling the person online. `watchIdleness` is the half only the app can
  // do -- the SDK cannot tell a keystroke from a cron job (agora#260). The
  // announcement is a statement of the current truth, never retried: it is
  // carried by the sync itself, so a refused `PUT` catches up on the next
  // poll.
  useEffect(() => {
    if (!chatAvailable || !chatClient) return;
    return watchIdleness({
      onChange: (next) => {
        void chatClient.announcePresence(next).catch(() => {});
      },
    });
  }, [chatAvailable, chatClient]);

  // Decision: `CalendarClientOptions.sessionUrl` is a JMAP session URL, and
  // Stalwart serves mail's and calendar's capabilities from the same
  // session document -- there is no separate calendar session endpoint in
  // this agora's config, so this reuses `mailSessionUrl(agora)` rather than
  // adding one. `getAccessToken` (not a static token) because this client,
  // like the chat client above, never caches the token itself.
  const calendarClientRef = useRef<CalendarClient | null>(null);
  if (!calendarClientRef.current) {
    calendarClientRef.current = createCalendarClient({
      sessionUrl: mailSessionUrl(agora),
      getAccessToken: () => accessTokenRef.current,
    });
  }
  const calendarClient = calendarClientRef.current;

  // A colleague's working hours, for Chat's member list (agora#258). Off
  // unless this company's config turns it on. The person id the hours are
  // keyed on is the local part of the chat user id (`@<id>:<server>`) -- the
  // id the issuer signs people in under, which chat provisions its users
  // from; see the PR's Findings for the assumption.
  const colleagueHoursRef = useRef<ColleagueHoursSource | null>(null);
  if (!colleagueHoursRef.current && hoursEnabled(agora)) {
    colleagueHoursRef.current = {
      client: createHoursClient({
        url: hoursUrl(agora),
        getAccessToken: () => accessTokenRef.current,
      }),
      personId: (chatUserId) => /^@([^:]+):/.exec(chatUserId)?.[1] ?? null,
    };
  }
  const colleagueHours = colleagueHoursRef.current;

  // The signed-in mailbox's address: its domain is the company's own mail domain.
  const [mailAddress, setMailAddress] = useState<string | null>(null);

  // Kept locally too (not only lifted through `onAccount`), so `Calendar`'s
  // own `meName` prop -- Find a time's own row -- has it without asking the
  // mail SDK a second time. Also `realFiles()`'s own `me` below, mirrored
  // into a ref (`accessTokenRef`'s own shape) so a name that resolves after
  // that source was built still reaches files listed or uploaded after it
  // does.
  const [mailName, setMailName] = useState<string | null>(null);
  const mailNameRef = useRef(mailName);
  mailNameRef.current = mailName;

  // One person card for every name in Mail, Chat and Calendar (agora#218).
  const people = useMemo<PeopleSource>(
    () => ({
      chat: chatAvailable ? chatClient : null,
      calendar: calendarClient,
      ...(calls ? { calls } : {}),
      mailDomain: mailAddress?.split('@')[1]?.toLowerCase() ?? null,
      selfAddress: mailAddress,
      selfName: mailName,
      openMail: (address) => go(pathForMailCompose(agora.id, address)),
      openRoom: (roomId) => go(pathForChat(agora.id, { roomId, threadId: null, eventId: null })),
    }),
    [chatAvailable, chatClient, calendarClient, calls, mailAddress, mailName, go, agora.id],
  );
  useEffect(() => {
    if (!client) {
      onAccount(null);
      setMailName(null);
      setMailAddress(null);
      return;
    }
    let live = true;
    fetchIdentities(client)
      .then((ids) => {
        if (!live) return;
        const identity = ids[0];
        const name = identity ? identity.name.trim() || null : null;
        onAccount(identity ? { name, address: identity.email } : null);
        setMailName(name);
        setMailAddress(identity?.email ?? null);
      })
      .catch(() => {
        // The section reports its own failure; the header just says nothing.
      });
    return () => {
      live = false;
      onAccount(null);
      setMailName(null);
      setMailAddress(null);
    };
  }, [client, onAccount]);

  // `fakeFiles()` for the preview/tests (`FILES_FAKE`); `realFiles()` once
  // this agora's config carries `filesProjectId` *and* this session's own
  // token actually carries that audience (`filesReady`, agora#353, agora#418);
  // neither otherwise -- the rail's own tile is gated the same way
  // (`AgoraShell`'s `filesAvailable`), so a stale `/files` link still falls
  // through to the "not turned on" message the disabled tile already
  // explains.
  const filesReadyNow = filesReady(agora, session);

  // The subject behind this session's access token, asked of the issuer
  // (`tokenSubject`, agora#417, agora#505) rather than decoded locally --
  // this agora's own token is opaque on purpose, so unlike before (agora#505)
  // this cannot be read off it directly. Held in state, not only a ref, so a
  // change clears it and rebuilds `filesRef` below: the effect resets it to
  // `null` the moment its own inputs change, *before* the async call
  // settles, so a refused or slow verification during a token rotation is
  // never mistaken for "nothing changed yet" and left showing the previous
  // person's Files client (agora#417 review, PR #436, agora#512).
  const [filesSubject, setFilesSubject] = useState<string | null>(null);
  // `agora.issuer` is the whole of `agora` this effect reads; naming the
  // object itself would re-ask userinfo on every cosmetic config rewrite
  // (name, theme), not only an issuer change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    setFilesSubject(null);
    if (FILES_FAKE || !filesReadyNow) return;
    let live = true;
    tokenSubject(agora, session.accessToken)
      .then((subject) => {
        if (live) setFilesSubject(subject);
      })
      .catch(() => {
        if (live) setFilesSubject(null);
      });
    return () => {
      live = false;
    };
  }, [filesReadyNow, agora.issuer, session.accessToken]);

  const filesRef = useRef<FilesSource | null>(null);
  // What `filesRef.current` was last built from -- every field a rebuilt
  // `realFiles()` depends on, not only the subject (agora#417 review): a
  // changed collection or organisation needs a new client exactly as much as
  // a changed person does, and a cleared subject (rotation or refusal, just
  // above) needs the previous client dropped, not kept showing a stale
  // person's files.
  const filesKeyRef = useRef<{
    subject: string;
    origin: string;
    organisationId: string;
    filesCollectionId: string | undefined;
  } | null>(null);
  if (FILES_FAKE) {
    if (!filesRef.current) filesRef.current = fakeFiles();
  } else {
    const nextKey = filesSubject
      ? {
          subject: filesSubject,
          origin: agoraOrigin(agora),
          organisationId: agora.organisationId,
          filesCollectionId: agora.filesCollectionId,
        }
      : null;
    const prevKey = filesKeyRef.current;
    const unchanged =
      prevKey === nextKey ||
      (prevKey !== null &&
        nextKey !== null &&
        prevKey.subject === nextKey.subject &&
        prevKey.origin === nextKey.origin &&
        prevKey.organisationId === nextKey.organisationId &&
        prevKey.filesCollectionId === nextKey.filesCollectionId);
    if (!unchanged) {
      filesKeyRef.current = nextKey;
      filesRef.current = nextKey
        ? realFiles({
            origin: nextKey.origin,
            organisationId: nextKey.organisationId,
            getAccessToken: () => accessTokenRef.current,
            me: () => ({ subject: nextKey.subject, name: mailNameRef.current ?? nextKey.subject }),
            collectionId: nextKey.filesCollectionId,
          })
        : null;
    }
  }
  const filesSource = filesRef.current;

  // Tasks: the real service through `/_tasks` once the company's config
  // turns it on; `null` otherwise, and the section says so. Keyed on
  // `tasksEnabled`, not only truthiness (agora#486): the company's own
  // config can flip live, carried by a fresh `agora` for this same
  // `agora.id` (the `prev.map` merge above `AgoraView`), with no remount
  // to reset this ref the way switching to a different agora does.
  const tasksRef = useRef<TasksSource | null>(null);
  const tasksEnabledRef = useRef<boolean | null>(null);
  const nowTasksEnabled = tasksEnabled(agora);
  if (tasksEnabledRef.current !== nowTasksEnabled) {
    tasksEnabledRef.current = nowTasksEnabled;
    tasksRef.current = TASKS_FAKE ? fakeTasks() : realTasks(agora, () => accessTokenRef.current);
  }
  const tasksSource = tasksRef.current;

  // The header's combined search reads these off `AgoraShell`'s own
  // `searchClients` prop, not through `AgoraView`'s children -- see
  // `AgoraShell.tsx`'s own doc for why the field lives in the shell. Chat
  // follows `chatAvailable` the same way the `chatClient` prop below does:
  // a token without the messaging audience never gets to search a room it
  // was never shown in the first place.
  useEffect(() => {
    onSearchClients({
      mail: client,
      calendar: calendarClient,
      chat: chatAvailable ? chatClient : null,
      files: filesSource,
    });
    return () => onSearchClients({ mail: null, calendar: null, chat: null, files: null });
  }, [client, calendarClient, chatAvailable, chatClient, filesSource, onSearchClients]);

  // The Mail tile's own badge -- live for as long as a client exists, not
  // only when Mail itself is the open section, since the rail is on screen
  // for every one of them. `watchMailUnread` (`counts.ts`) is what keeps it
  // live: an immediate read, then a refetch on every JMAP push that says
  // the account's `Mailbox` state changed -- reading a mail decrements this
  // the same way a new one increments it (agora#219).
  useEffect(() => {
    if (!client) {
      onMailUnread(null);
      return;
    }
    const stop = watchMailUnread(client, onMailUnread);
    return () => {
      stop();
      onMailUnread(null);
    };
  }, [client, onMailUnread]);

  // The Chat tile's own badge -- gated on `chatAvailable` the same way the
  // header search's own `chat` client is just above, since a client whose
  // exchange was refused never has a meaningful room list.
  useEffect(() => {
    if (!chatAvailable || !chatClient) {
      onChatUnread(null);
      return;
    }
    const stop = watchChatUnread(chatClient, onChatUnread);
    return () => {
      stop();
      onChatUnread(null);
    };
  }, [chatAvailable, chatClient, onChatUnread]);

  // The Calendar tile's own badge.
  useEffect(() => {
    if (!calendarClient) {
      onCalendarUnread(null);
      return;
    }
    const stop = watchCalendarNeedsAction(calendarClient, onCalendarUnread);
    return () => {
      stop();
      onCalendarUnread(null);
    };
  }, [calendarClient, onCalendarUnread]);

  // Meeting reminders (T6, D7): one OS notification per upcoming event this
  // account accepted or organises, live for as long as a calendar client
  // exists -- independent of whether Calendar is the open section, the same
  // reasoning the rail's own badge effect above already uses. `reminders.ts`
  // itself no-ops without notification permission, so this stays mounted
  // whether or not the person has granted it yet.
  useEffect(() => {
    if (!calendarClient) return;
    return watchReminders(calendarClient, agora.id, (event) => {
      showReminderNotification(event, { agoraId: agora.id, go });
    });
  }, [calendarClient, agora.id, go]);

  // The chat section's own currently-open room, for `watchChatNotifications`
  // below to read fresh on every event rather than capture once -- `route`
  // changes on nearly every navigation, far more often than the watcher
  // itself needs to restart. Same "ref beside a value that outlives its own
  // effect" shape as `accessTokenRef` above.
  const currentChatRoomIdRef = useRef<string | null>(
    route.section === 'chat' ? route.location.roomId : null,
  );
  currentChatRoomIdRef.current = route.section === 'chat' ? route.location.roomId : null;

  // In-app chat notifications (T4, MSG-022, and the notification half of
  // MSG-034): one OS notification per mention or direct message, live for as
  // long as chat is available -- the same lifecycle the Chat tile's own
  // badge effect above already uses, since a token without the messaging
  // audience has no rooms worth watching either.
  useEffect(() => {
    if (!chatAvailable || !chatClient) return;
    return watchChatNotifications(chatClient, {
      agoraId: agora.id,
      go,
      currentRoomId: () => currentChatRoomIdRef.current,
    });
  }, [chatAvailable, chatClient, agora.id, go]);

  // Ring when another member starts a call in a room (calls on only).
  useIncomingCalls(chatAvailable ? chatClient : null, calls, { agoraId: agora.id, go });

  if (!client) return null;

  if (route.section === 'today') {
    return (
      <Today
        agoraId={agora.id}
        mailClient={client}
        calendarClient={calendarClient}
        chatClient={chatAvailable ? chatClient : null}
        callsEnabled={Boolean(calls)}
        go={go}
      />
    );
  }
  if (route.section === 'calendar') {
    if (!calendarClient) return null;
    return (
      <PeopleProvider value={people}>
        <Calendar
          client={calendarClient}
          mailClient={client}
          agoraId={agora.id}
          location={route.location}
          meName={mailName}
          {...(calls ? { calls } : {})}
          go={go}
          onNavigate={(to) => go(pathForCalendar(agora.id, to))}
          onError={(error) => {
            if (error.status !== 401) {
              toast(error.message, 'bad');
              return;
            }
            if (refusedOnce) return; // Already tried a refresh for this refusal; do not loop.
            setRefusedOnce(true);
            void onRefresh()
              .then((refreshed) => {
                // A stale completion belongs to an ended lifecycle, not this one's
                // refusal: release the lock so this still-mounted view's next 401
                // (for example after a same-company config rewrite) gets its own
                // refresh instead of being silently dropped by `refusedOnce`.
                if (refreshed === null) {
                  setRefusedOnce(false);
                  return;
                }
                if (refreshed) {
                  setRefusedOnce(false);
                  return;
                }
                onSignIn();
              })
              // An unhandled rejection here would both drop the error and leave
              // `refusedOnce` latched, silently swallowing every later 401 on
              // this still-mounted view.
              .catch((error: unknown) => {
                setRefusedOnce(false);
                console.error('Refresh after a calendar refusal failed:', error);
              });
          }}
        />
      </PeopleProvider>
    );
  }

  // Chat's own audience gate: the rail already leaves Chat out for a token
  // without the audience, but a stale link may still point at `/chat`.
  if (route.section === 'chat') {
    if (chatState === 'asking') {
      return (
        <section className="grid flex-1 content-start gap-4 p-8">
          <p className="m-0 text-muted" role="status">
            Opening Chat…
          </p>
        </section>
      );
    }
    if (!chatAvailable || !chatClient) {
      return (
        <section className="grid flex-1 content-start gap-4 p-8">
          <Empty icon="chat">{agora.name} has not turned Chat on for this account.</Empty>
        </section>
      );
    }
    // The full-results view (agora#269): reached at
    // `/h/<agoraId>/chat/search/<query>`, what Enter opens from the header
    // search when nothing is highlighted. Rendered here rather than inside
    // `Chat` itself, which stays untouched by this ticket.
    if (route.location.query !== undefined) {
      return (
        <ChatSearchResults
          client={chatClient}
          agoraId={agora.id}
          query={route.location.query}
          onOpen={(row) => go(pathForRoute(row.route))}
        />
      );
    }
    return (
      <PeopleProvider value={people}>
        <Chat
          client={chatClient}
          location={route.location}
          onNavigate={(to) => go(pathForChat(agora.id, to))}
          linkFor={(to) => new URL(pathForChat(agora.id, to), window.location.origin).href}
          calls={calls}
          encryption={encryptedChannelsEnabled(agora)}
          {...(colleagueHours ? { hours: colleagueHours } : {})}
        />
      </PeopleProvider>
    );
  }

  // Files' own gate, the same shape as Chat's above: the rail already
  // leaves the tile disabled without a source, but a stale link may still
  // point at `/files`.
  if (route.section === 'files') {
    if (!filesSource) {
      return (
        <section className="grid flex-1 content-start gap-4 p-8">
          <Empty icon="folder">{agora.name} has not turned Files on for this account.</Empty>
        </section>
      );
    }
    return (
      <Files
        source={filesSource}
        companyName={agora.name}
        location={route.location}
        onNavigate={(to) => go(pathForFiles(agora.id, to))}
      />
    );
  }

  if (route.section === 'tasks') {
    if (!tasksSource) {
      return (
        <section className="grid flex-1 content-start gap-4 p-8">
          <Empty icon="check">{TASKS_OFF_MESSAGE}</Empty>
        </section>
      );
    }
    return (
      <Tasks
        source={tasksSource}
        location={route.location}
        onNavigate={(to) => go(pathForTasks(agora.id, to))}
      />
    );
  }

  return (
    <PeopleProvider value={people}>
      <Mail
        client={client}
        clientFor={clientFor}
        calendarClient={calendarClient}
        chatClient={chatAvailable ? chatClient : null}
        agoraId={agora.id}
        trustedAuthServId={agora.mailAuthServId}
        location={route.location}
        onNavigate={(to) => go(pathForMail(agora.id, to))}
        onActions={onActions}
        onTitle={onTitle}
        onError={(error) => {
          if (error.status !== 401) {
            toast(error.message, 'bad');
            return;
          }
          if (refusedOnce) return; // Already tried a refresh for this refusal; do not loop.
          setRefusedOnce(true);
          void onRefresh()
            .then((refreshed) => {
              // A stale completion belongs to an ended lifecycle, not this one's
              // refusal: release the lock so this still-mounted view's next 401
              // (for example after a same-company config rewrite) gets its own
              // refresh instead of being silently dropped by `refusedOnce`.
              if (refreshed === null) {
                setRefusedOnce(false);
                return;
              }
              if (refreshed) {
                setRefusedOnce(false);
                return;
              }
              onSignIn();
            })
            // An unhandled rejection here would both drop the error and leave
            // `refusedOnce` latched, silently swallowing every later 401 on
            // this still-mounted view.
            .catch((error: unknown) => {
              setRefusedOnce(false);
              console.error('Refresh after a mail refusal failed:', error);
            });
        }}
      />
    </PeopleProvider>
  );
}
